import { FC, useState, useMemo, useEffect } from "react";
import {
  Modal,
  Row,
  Col,
  Tag,
  Divider,
  Input,
  Button,
  Space,
  DatePicker,
  Radio,
  Select,
  Table,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import dayjs from "dayjs";
import { IPatientCaseTypesState } from "../../../interfaces/ICase";
import ConfirmRegIdHistoryModal from "./ConfirmRegIdHistoryModal";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import { patchClaimId } from "../../../models/DischargeSlice";
import { getBasicDetails } from "../../../models/patientSlice";
import {
  getPortalDataCghs,
  getPortalDataCghsNew,
  getPortalDataEchs,
  getPortalDataIhx,
  getPortalDataStarhealth,
} from "../../../models/preAuthSlice";
import { getPortalDataForAyushB } from "../../../models/ayushmanBSlice";

type RegistrationValidationModalProps = {
  open: boolean;
  onCancel: () => void;
  validationRes: any;
  onUpdate?: (updatedG7: any) => void | Promise<void>;
  totalCases: Array<IPatientCaseTypesState>;
};

const RegistrationValidationModal: FC<RegistrationValidationModalProps> = ({
  open,
  onCancel,
  validationRes,
  onUpdate,
  totalCases,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { patients, preAuth, ayushBPreAuth } = useSelector((state: RootState) => state);
  const dateFormat = "DD-MM-YYYY";
  const [activeTab, setActiveTab] = useState("1");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRegistrationIdUpdationLoading, setIsRegistrationIdUpdationLoading] =
    useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] =
    useState<string>("");
  const dispatch = useDispatch<AppDispatch>();

  // Local editable G7 state
  const [g7Data, setG7Data] = useState<any>(() => ({
    patientName: patients?.fetchBasicDetails?.patientResponse?.fullName,
    // age: patients?.fetchBasicDetails?.patientResponse?.age,
    programId:
      preAuth?.portalData?.beneficiaryId || ayushBPreAuth?.portalData?.beneficiaryId ||
      patients?.fetchBasicDetails?.casesResponse?.memberId ||
      patients?.fetchBasicDetails?.referralInfoResponse?.cghsReferralResponse
        ?.beneficiaryId ||
      patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
    registrationId: preAuth?.portalData?.schemeRegistrationId || ayushBPreAuth?.portalData?.schemeRegistrationId,
    // registrationDate: patients?.fetchBasicDetails?.visitInfoResponse?.doa,
    registrationDate: preAuth?.portalData?.registrationDate
      ? preAuth?.portalData?.registrationDate
      : ayushBPreAuth?.portalData?.registrationDate
      ? ayushBPreAuth?.portalData?.registrationDate
      : patients?.fetchBasicDetails?.visitInfoResponse?.doa
        ? patients?.fetchBasicDetails?.visitInfoResponse?.doa
        : null,
    gender: patients?.fetchBasicDetails?.patientResponse?.sex,
    visitType: patients?.fetchBasicDetails?.visitInfoResponse?.visitType,
  }));

  useEffect(() => {
    setG7Data({
      ...g7Data,
      registrationId: preAuth?.portalData?.schemeRegistrationId || ayushBPreAuth?.portalData?.schemeRegistrationId || preAuth?.portalData?.registrationId,
      programId:
        preAuth?.portalData?.beneficiaryId ||
        ayushBPreAuth?.portalData?.beneficiaryId ||
        patients?.fetchBasicDetails?.casesResponse?.memberId ||
        patients?.fetchBasicDetails?.referralInfoResponse?.cghsReferralResponse
          ?.beneficiaryId ||
        patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
      registrationDate: preAuth?.portalData?.registrationDate
        ? preAuth?.portalData?.registrationDate
        : ayushBPreAuth?.portalData?.registrationDate
          ? ayushBPreAuth?.portalData?.registrationDate
          : patients?.fetchBasicDetails?.visitInfoResponse?.doa
            ? patients?.fetchBasicDetails?.visitInfoResponse?.doa
            : null,
    });
  }, [
    preAuth?.portalData?.schemeRegistrationId,
    preAuth?.portalData?.registrationDate,
    ayushBPreAuth?.portalData?.registrationDate,
    preAuth?.portalData,
  ]);

  useEffect(() => {
    if (!open) {
      setActiveTab("1");
      setIsConfirmModalOpen(false);
      setSelectedRegistrationId("");
      setIsRegistrationIdUpdationLoading(false);
    }
  }, [open]);

  const handleChange = (field: string, value: any) => {
    if (field === "caseType") {
      let visitType = value
        ?.split("~")?.[1]
        ?.toUpperCase()
        ?.includes("EMERGENCY")
        ? "EMERGENCY"
        : value?.split("~")[1]?.toUpperCase()?.includes("OP") ||
            value?.split("~")[1]?.toUpperCase()?.includes("DIALYSIS")
          ? "OP"
          : "IP";

      setG7Data({ ...g7Data, caseType: value?.split("~")?.[0], visitType });
    } else if (field === "registrationDate")
      setG7Data({
        ...g7Data,
        [field]: value
          ? dayjs(value, "DD-MM-YYYY").format("DD-MM-YYYY HH:mm:ss")
          : null,
      });
    else setG7Data({ ...g7Data, [field]: value });
  };

  const handleUpdate = async () => {
    if (onUpdate) await onUpdate(g7Data);
    setIsEditing(false);
  };

  // Detect discrepancy for any field
  const hasMismatch = useMemo(() => {
    const fields = [
      "patientName",
      // "age",
      "programId",
      "registrationId",
      "registrationDate",
      "gender",
      "visitType",
    ];

    return fields?.some((key) => {
      const portalValue = validationRes?.patientDto?.[key];
      let g7Value = g7Data?.[key];

      if (key === "registrationDate") {
        if (!portalValue && !g7Value) return false;

        const portalDay = dayjs(portalValue, [
          "DD/MM/YYYY",
          "DD-MM-YYYY",
          "YYYY-MM-DD",
        ]);

        const g7Day = dayjs(g7Value, [
          "DD-MM-YYYY HH:mm:ss",
          "DD/MM/YYYY HH:mm:ss",
          "DD-MM-YYYY",
          "DD/MM/YYYY",
        ]);

        if (!portalDay.isValid() || !g7Day.isValid()) {
          return true;
        }

        return !portalDay.isSame(g7Day, "day");
      }

      if (key === "visitType") {
        if (g7Value?.toString()?.toLowerCase() === "emergency") {
          g7Value = "IP";
        }
      }

      return (
        portalValue?.toString()?.toLowerCase()?.trim() !==
        g7Value?.toString()?.toLowerCase()?.trim()
      );
    });
  }, [validationRes, g7Data]);

  // Determine status
  // const derivedStatus = hasMismatch ? "DISCREPANCY" : validationRes?.status;
  const derivedStatus = isEditing
    ? "DISCREPANCY"
    : hasMismatch
      ? "DISCREPANCY"
      : validationRes?.status;

  const renderFieldRow = (label: string, fieldKey: string) => {
    const portalValue = validationRes?.patientDto?.[fieldKey];
    let g7Value = g7Data?.[fieldKey];

    let isMismatch;

    if (fieldKey === "registrationDate") {
      const portalDay = dayjs(portalValue, [
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "YYYY-MM-DD",
      ]);

      const g7Day = dayjs(g7Value, [
        "DD-MM-YYYY HH:mm:ss",
        "DD/MM/YYYY HH:mm:ss",
        "DD-MM-YYYY",
        "DD/MM/YYYY",
      ]);

      isMismatch =
        !portalDay.isValid() ||
        !g7Day.isValid() ||
        !portalDay.isSame(g7Day, "day");
    } else if (fieldKey === "visitType") {
      if (g7Value?.toString()?.toLowerCase() === "emergency") {
        isMismatch =
          portalValue?.toString()?.toLowerCase()?.trim() !==
          "IP"?.toString()?.toLowerCase()?.trim();
      } else {
        isMismatch =
          portalValue?.toString()?.toLowerCase()?.trim() !==
          g7Value?.toString()?.toLowerCase()?.trim();
      }
    } else {
      isMismatch =
        portalValue?.toString()?.toLowerCase()?.trim() !==
        g7Value?.toString()?.toLowerCase()?.trim();
      // portalValue?.toString()?.trim() !== g7Value?.toString()?.trim();
    }

    if (fieldKey === "registrationDate" && g7Value) {
      g7Value = g7Value ? dayjs(g7Value, dateFormat).format("DD-MM-YYYY") : "-";
    }

    // const isMismatch =
    //   portalValue?.toString()?.trim() !== g7Value?.toString()?.trim();

    return (
      <Row
        gutter={16}
        style={{
          background: "#fff",
          padding: "8px 12px",
          borderRadius: 8,
          marginBottom: 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Col span={8}>
          <strong>{label}</strong>
        </Col>

        <Col span={8}>
          <span>{portalValue || "-"}</span>
        </Col>

        <Col
          span={8}
          style={{
            color: isMismatch ? "#d92955ff" : "inherit",
            borderRadius: 6,
            padding: isEditing ? 0 : "6px 0",
            transition: "0.3s",
          }}
        >
          {isEditing ? (
            fieldKey === "registrationDate" ? (
              <DatePicker
                format={dateFormat}
                value={g7Value ? dayjs(g7Value, dateFormat, true) : null}
                onChange={(date, dateString) =>
                  handleChange(fieldKey, dateString)
                }
                style={{ width: "100%" }}
              />
            ) : fieldKey === "gender" ? (
              <Radio.Group
                name="gender"
                value={g7Value}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
              >
                <Radio value="Male">MALE</Radio>
                <Radio value="Female">FEMALE</Radio>
              </Radio.Group>
            ) : fieldKey === "visitType" ? (
              <Select
                showSearch
                placeholder="Select a case type"
                options={
                  totalCases
                    ? totalCases?.map((caseType) => ({
                        value: caseType?.id + "~" + caseType?.name,
                        label: caseType?.name,
                      }))
                    : []
                }
                defaultValue={
                  totalCases?.find(
                    (caseType) =>
                      caseType?.name?.toUpperCase() ===
                      patients?.fetchBasicDetails?.casesResponse?.caseTypeMetaDataName?.toUpperCase(),
                  )?.name
                }
                style={{ width: "100%" }}
                onChange={(caseType: any) => {
                  handleChange("caseType", caseType);
                }}
                allowClear
                // disabled={props?.isAddCase}
              />
            ) : (
              <Input
                value={g7Value}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
              />
            )
          ) : (
            <span>{g7Value || "-"}</span>
          )}
        </Col>
      </Row>
    );
  };

  const handleRadioChange = (registrationId: string) => {
    if (selectedRegistrationId === registrationId) {
      setSelectedRegistrationId("");
    } else {
      setSelectedRegistrationId(registrationId);
    }
  };

  const headerStyle = { color: "rgb(0, 76, 143)" };

  const historyTableColumns = [
    {
      title: <strong style={headerStyle}>Name</strong>,
      dataIndex: "name",
      key: "name",
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: <strong style={headerStyle}>Gender</strong>,
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: <strong style={headerStyle}>Date of Birth</strong>,
      dataIndex: "dateofbirth",
      key: "dateofbirth",
    },
    {
      title: <strong style={headerStyle}>Registration Id</strong>,
      dataIndex: "registrationid",
      key: "registrationid",
    },
    {
      title: <strong style={headerStyle}>Registration Date</strong>,
      dataIndex: "registrationdate",
      key: "registrationdate",
    },
    {
      title: <strong style={headerStyle}>Portal status</strong>,
      dataIndex: "statusName",
      key: "statusName",
    },
    {
      title: <strong style={headerStyle}>Action</strong>,
      key: "action",
      render: (_: any, record: any) => {
        return (
          <Radio
            checked={record?.registrationid === selectedRegistrationId}
            disabled={
              selectedRegistrationId &&
              record?.registrationid !== selectedRegistrationId
                ? true
                : false
            }
            onClick={() => handleRadioChange(record?.registrationid)}
          />
        );
      },
    },
  ];

  const historyDataSource =
    validationRes?.beneficiaryCases?.map((item: any, index: number) => ({
      key: index, // better if you have unique id
      ...item,
    })) || [];

  const refreshPortalData = async (caseId: number, visitId: number) => {
    const portalName =
      patients?.fetchBasicDetails?.casesResponse?.portalMetaDataNames?.[0];

    if (portalName === "IHX") {
      await dispatch(getPortalDataIhx({ caseId, visitId }));
    } else if (portalName === "CGHS") {
      await dispatch(getPortalDataCghs({ caseId, visitId }));
    } else if (portalName === "CGHS AB") {
      await dispatch(getPortalDataCghsNew({ caseId, visitId }));
    } else if (portalName === "ECHS") {
      await dispatch(getPortalDataEchs({ caseId, visitId }));
    } else if (portalName === "STAR HEALTH") {
      await dispatch(getPortalDataStarhealth({ caseId, visitId }));
    } else if (portalName === "Ayushman Bharat") {
      await dispatch(getPortalDataForAyushB({ caseId, visitId }));
    }
  };

  const handleOk = async () => {
    try {
      setIsRegistrationIdUpdationLoading(true);
      const updatedFormData = {
        registrationId: selectedRegistrationId,
        claimId: selectedRegistrationId,
      };
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const schemeId =
        patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

      await dispatch(
        patchClaimId({
          formData: updatedFormData,
          caseId,
          visitId,
          schemeId,
        }),
      ).unwrap();

      if (caseId && visitId) {
        await refreshPortalData(caseId, visitId);
        await dispatch(getBasicDetails({ id: visitId }));
      }

      successMessage("Registration Id has been updated successfully.");
      setIsConfirmModalOpen(false);
      setIsRegistrationIdUpdationLoading(false);
    } catch (err) {
      setIsRegistrationIdUpdationLoading(false);
      errorMessage("Failed to update registration Id. Please try again later.");
    }
  };

  const handleRegistrationCancel = () => {
    setIsConfirmModalOpen(false);
  };

  return (
    <Modal
      // title={
      //   <div style={{ fontSize: 18, fontWeight: 600 }}>
      //     Patient Registration Validation
      //   </div>
      // }
      open={open}
      footer={null}
      onCancel={onCancel}
      width={1000}
      bodyStyle={{ background: "#fafafa", padding: "20px 25px" }}
    >
      <Row
        gutter={20}
        style={{
          borderBottom: "0.4px solid #D1D1D1",
          marginBottom: 20,
        }}
      >
        {[
          { key: "1", label: "Patient Registration Validation" },
          { key: "2", label: "Registration History" },
        ].map((tab) => (
          <Col key={tab.key}>
            <Button
              type={activeTab === tab.key ? "primary" : "default"}
              onClick={() => setActiveTab(tab.key)}
              style={
                activeTab === tab.key
                  ? {
                      background: "#00549d",
                      borderTopLeftRadius: "15px",
                      borderTopRightRadius: "15px",
                      borderBottomLeftRadius: "0px",
                      borderBottomRightRadius: "0px",
                      fontWeight: "bold",
                      height: "36px",
                      boxShadow: "0px -1px 4px 0px #00000040 inset",
                    }
                  : {
                      border: "none",
                      boxShadow: "none",
                      height: "36px",
                      color: "#575757",
                      fontWeight: 600,
                    }
              }
            >
              {tab.label}
            </Button>
          </Col>
        ))}
      </Row>

      {/* TAB 1 - YOUR ORIGINAL CODE (UNCHANGED) */}
      {activeTab === "1" && (
        <>
          <Row style={{ marginBottom: 10 }}>
            <Col span={8}>
              <strong style={{ color: "rgb(0, 76, 143)" }}>Field</strong>
            </Col>
            <Col span={8}>
              <strong style={{ color: "rgb(0, 76, 143)" }}>
                Portal Details
              </strong>
            </Col>
            <Col span={8}>
              <strong style={{ color: "rgb(0, 76, 143)" }}>
                G7 Pro Details
              </strong>
            </Col>
          </Row>

          {/* Comparison */}
          {renderFieldRow("Patient Name", "patientName")}
          {/* {renderFieldRow("Age", "age")} */}
          {renderFieldRow("Program ID", "programId")}
          {renderFieldRow("Registration ID", "registrationId")}
          {renderFieldRow("Registration Date", "registrationDate")}
          {renderFieldRow("Gender", "gender")}
          {renderFieldRow("Visit Type", "visitType")}

          <Divider />

          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <strong>Status: </strong>
            {derivedStatus === "VALIDATED" ? (
              <Tag
                color="success"
                style={{ fontSize: 15, padding: "6px 15px" }}
              >
                VALIDATED
              </Tag>
            ) : derivedStatus === "DISCREPANCY" ? (
              <Tag color="error" style={{ fontSize: 15, padding: "6px 15px" }}>
                MISMATCH
              </Tag>
            ) : (
              <Tag color="error" style={{ fontSize: 15, padding: "6px 15px" }}>
                NOT VALIDATED
              </Tag>
            )}
          </div>

          {!isEditing &&
          validationRes?.status === "VALIDATED" &&
          !hasMismatch ? (
            <></>
          ) : (
            <div style={{ textAlign: "center" }}>
              <Space>
                {!isEditing ? (
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "rgb(0, 76, 143)",
                      color: "#fff",
                      boxShadow: "0px 2px 8px 0px #00000040",
                    }}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit G7 Details
                  </Button>
                ) : (
                  <>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "rgb(0, 76, 143)",
                        color: "#fff",
                        boxShadow: "0px 2px 8px 0px #00000040",
                      }}
                      onClick={handleUpdate}
                    >
                      Update
                    </Button>
                    <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                  </>
                )}
              </Space>
            </div>
          )}
        </>
      )}

      {/* TAB 2 */}
      {activeTab === "2" && (
        <>
          <Table
            columns={historyTableColumns}
            dataSource={historyDataSource}
            pagination={false}
            bordered
            scroll={{ y: 350 }}
          />
          <div style={{ textAlign: "center", marginTop: 22 }}>
            <Button
              type="primary"
              disabled={!selectedRegistrationId}
              style={{
                backgroundColor: selectedRegistrationId
                  ? "rgb(0, 76, 143)"
                  : "#d9d9d9", // disabled bg
                borderColor: selectedRegistrationId
                  ? "rgb(0, 76, 143)"
                  : "#d9d9d9",
                color: selectedRegistrationId ? "#fff" : "#8c8c8c", // readable text
                boxShadow: selectedRegistrationId
                  ? "0px 2px 8px 0px #00000040"
                  : "none",
                cursor: selectedRegistrationId ? "pointer" : "not-allowed",
              }}
              onClick={() => setIsConfirmModalOpen(true)}
            >
              Update Registration ID
            </Button>
          </div>

          <ConfirmRegIdHistoryModal
            open={isConfirmModalOpen}
            onOk={handleOk}
            onCancel={handleRegistrationCancel}
            isRegistrationIdUpdationLoading={isRegistrationIdUpdationLoading}
          />
        </>
      )}
    </Modal>
  );
};

export default RegistrationValidationModal;
